import React, { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { Button } from "Components";
import StepFooter from "../../components/StepFooter";
import { DIFFICULTY_COLOR } from "../../PhishingManager.constants";
import { getCampaignReviewData } from "PhishingManager/service/index.Service";
import { capitalize, errorMessageHandler, joinItems } from "PhishingManager/utils";


type CampaignData = {
  id: number | null;
  packaged_template: {
    id: number;
    name: string;
    difficulty: string;
    categories: string[];
    image_path: string;
  },
  test_recipients: string[],
  full_campaign_audience: {
    selected_type: string;
    values: string | string[];
  },
  delivery_and_schedule: {
    start_date: string;
    start_time: string;
    campaign_length: number;
    timezone: string;
  }
}

interface StepProps {
  tenantId: number | string | undefined;
  campaignId: string | number | null;
  showSummary: boolean;
  onBack: () => void;
  onFinish: () => void;
  showPreview: (id: string | number) => void;
  updateName?: (name: string) => void;
  setToast: (
    toast: { message: string; type: 'warning' | 'success' }
  ) => void;
}

const Step5ReviewStart: React.FC<StepProps> = ({
  tenantId,
  campaignId,
  onBack,
  onFinish,
  showPreview,
  showSummary,
  updateName,
  setToast
}) => {
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const tag = capitalize(campaignData?.packaged_template?.difficulty || '') || 'Beginner';
  const duration = campaignData?.delivery_and_schedule?.campaign_length || 0;
  const { bg, color } = DIFFICULTY_COLOR[tag];
  const templateId = campaignData?.packaged_template?.id || 0;
  const orgType = campaignData?.full_campaign_audience?.selected_type || "";
  const audienceLabel = useMemo(() => {
    let label = "Properties";
    if (orgType == "groups") label = "Groups";
    else if (orgType == "roles") label = "Roles";
    else if (orgType == "organization") label = "Users";
    else if (orgType == "individuals") label = "Individuals";

    return `${label} selected`;
  }, [orgType])

  useEffect(() => {
    const getReviewData = async () => {
      try {
        if (!tenantId || !campaignId) return;
        setIsLoading(true);
        const res = await getCampaignReviewData(tenantId, campaignId);
        setCampaignData({ id: Number(campaignId), ...(res?.data || {}) });
        if(updateName) updateName(res?.data?.packaged_template?.name || '');
      } catch (error) {
        const msg = errorMessageHandler(error);
        setToast({ message: msg, type: 'warning' });
        setCampaignData(null);
      } finally {
        setIsLoading(false);
      }
    }
    getReviewData();
  }, [])

  return (
    <Spin spinning={isLoading}>
      <div className="review-container step-card">
        {campaignData ? <>
          {!showSummary &&
            <div className="card-header">
              <div className="card-title">Review & Start Test</div>
              <div className="card-subtitle">
                Review the configuration below and start the test campaign
              </div>
            </div>}

          <StepDetailSection title="Scenario">
            <div className="template-preview">
              <div className="pm-card-icon">
                <img alt={campaignData?.packaged_template?.name || ''} src={campaignData?.packaged_template?.image_path || ''} />
              </div>
              <div className="campaign-card">
                <span className="campaign-card-title">{campaignData?.packaged_template?.name || ''}</span>
                <span className="campaign-card-status" style={{ color, background: bg }}>{tag}</span>
                <span className="campaign-card-tag">{joinItems(campaignData?.packaged_template?.categories || '')}</span>
              </div>
              {templateId && <Button shape="round" type="primary" ghost onClick={() => showPreview && showPreview(templateId)}>Preview</Button>}
            </div>
          </StepDetailSection>

          <StepDetailSection title="Test Configuration">
            <CardStep title="Test recipients (required)" subTitle={joinItems(campaignData?.test_recipients || '')} />
          </StepDetailSection>

          <StepDetailSection title="Full Campaign Audience">
            <>
              <CardStep title="Selected method" subTitle={orgType} doCapitalize={true} />
              <CardStep title={audienceLabel} subTitle={joinItems(campaignData?.full_campaign_audience?.values || '')} doCapitalize={true} />
            </>
          </StepDetailSection>

          <StepDetailSection title="Delivery & Schedule" cardClass="card-grid">
            <>
              <CardStep title="Start date" subTitle={campaignData?.delivery_and_schedule?.start_date || ''} />
              <CardStep title="Start time" subTitle={campaignData?.delivery_and_schedule?.start_time || ''} />
              <CardStep title="Duration" subTitle={`${duration} day${Number(duration) > 1 ? 's' : ''}`} />
              <CardStep title="Timezone" subTitle={campaignData?.delivery_and_schedule?.timezone || ''} />
            </>
          </StepDetailSection>
        </> : <div className="cs-empty-state">
          {isLoading ? "Loading..." : "No Campaign Data Found."}
        </div>}
      </div>
      {!showSummary &&
        <StepFooter
          nextBtnText="Start test"
          onBack={onBack}
          onNext={onFinish}
          nextBtnDisabled={!campaignData}
        />}
    </Spin>
  );
};

export default Step5ReviewStart;

const StepDetailSection = ({ title, children, cardClass = '' }: { title: string, cardClass?: string, children: React.ReactElement }) => {
  return <div className="step-detail-section">
    <span className="step-title">{title}</span>
    <div className={`card ${cardClass}`}>{children}</div>
  </div>
}

const CardStep = ({ title, subTitle, doCapitalize = false }: { title: string, subTitle: string, doCapitalize?: boolean }) => {
  return <div className="card-step">
    <div className="card-step-title">{title}</div>
    <div className="card-step-subtitle" style={doCapitalize ? { textTransform: "capitalize" } : {}}>{subTitle}</div>
  </div>
}